import { Check, Eye, Heart, HeartPulse, Trash2 } from 'lucide-react';
import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Text,
  Title,
  Tooltip,
  Transition,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useFetch } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useHeartRateStore } from '../store/useHeartRateStore.ts';

export function CompletedSessions() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { events } = useHeartRateStore();
  const [deleteInProgress, setDeleteInProgress] = useState<number>();
  const [mountedCards, setMountedCards] = useState<number[]>([]);
  const [opened, { open, close }] = useDisclosure(false);
  const { data, loading, refetch } = useFetch<{ data: any[] }>(
    `${import.meta.env.VITE_API_URL}/services/completedConfigurations`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  useEffect(() => {
    if (data?.data) {
      data.data.forEach((_, index) => {
        setTimeout(() => {
          setMountedCards(prev => [...prev, index]);
        }, 50 * index); // Add a delay of 50ms per card
      });
    }
  }, [data]);

  const confirmDelete = (id: number) =>
    modals.openConfirmModal({
      title: t(
        'pa_confirm_delete',
        'Confermi di voler cancellare questa configurazione?'
      ),
      children: (
        <Text size='sm'>
          {t(
            'pa_confirm_delete_txt',
            'Se hai intenzione di cancellare la configurazione selezionata clicca conferma, altrimenti annulla per tornare indietro.'
          )}
        </Text>
      ),
      labels: {
        confirm: t('confirm', 'Conferma'),
        cancel: t('cancel', 'Annulla'),
      },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => deleteConfiguration(id),
    });

  const deleteConfiguration = (id: number) => {
    setDeleteInProgress(id);
    fetch(`${import.meta.env.VITE_API_URL}/services/configurations/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          // Throw an error for HTTP errors
          return response.text().then(text => {
            throw new Error(`HTTP ${response.status}: ${text}`);
          });
        }
      })
      .then(() => {
        refetch();
      })
      .catch(error => {
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'An error occurred',
          autoClose: false,
          message: error.message,
        });
      })
      .finally(() => {
        setDeleteInProgress(undefined);
      });
  };

  return (
    <>
      <Flex
        justify='space-between'
        mb={'sm'}
        direction={{ base: 'column', lg: 'row' }}
        align={{ base: 'flex-start', lg: 'center' }}
        gap={10}
      >
        <Flex direction={'column'}>
          <Title>{t('pa_title', 'GESTIONE SESSIONI IN REALTÀ VIRTUALE')}</Title>
          <Title order={4} c={'dimmed'}>
            Prepara qui le sessioni da riprodurre con gli utenti attraverso il
            visore di Realtà Virtuale
          </Title>
        </Flex>
        <Flex gap='xs'>
          <Button
            size={'small'}
            variant={'light'}
            component={Link}
            to={'/sessions'}
          >
            {t('pa_active_s', 'Sessioni attive')}
          </Button>
          <Button onClick={open} size='sm' variant='outline'>
            Scarica Manuali
          </Button>
        </Flex>
      </Flex>
      <Divider mb={'md'} mt={'md'} />
      <Card withBorder mb='md' py={21}>
        <Title order={4}>
          {t('pa_completed_sessions', 'Sessioni completate')}
        </Title>
      </Card>

      <Modal
        opened={opened}
        onClose={close}
        title='Seleziona il manuale da scaricare'
        centered
      >
        <Flex direction='column' gap='sm'>
          <Button
            component='a'
            href='https://become-hub.s3.eu-central-1.amazonaws.com/pdf/BECOME+-+Manuale+-+Augmented+Life+Experience.pdf'
            target='_blank'
            variant='light'
            fullWidth
          >
            Manuale - Augmented Life Experience
          </Button>

          <Button
            component='a'
            href='https://become-hub.s3.eu-central-1.amazonaws.com/pdf/BECOME+-+Manuale+-+Augmented+Food+Exposure.pdf'
            target='_blank'
            variant='light'
            fullWidth
          >
            Manuale - Augmented Food Exposure
          </Button>

          <Button
            component='a'
            href='https://become-hub.s3.eu-central-1.amazonaws.com/pdf/BECOME+-+Manuale+-+Augmented+Performance.pdf'
            target='_blank'
            variant='light'
            fullWidth
          >
            Manuale - Augmented Performance
          </Button>
        </Flex>
      </Modal>

      {loading && (
        <Flex align={'center'}>
          <Loader />
        </Flex>
      )}

      {data?.data.length === 0 && (
        <Text>{t('pa_no_sessions', 'Nessuna sessione trovata')}</Text>
      )}
      {data?.data?.map((c: any, index: number) => {
        const event =
          c.status === 'in-progress'
            ? events
                .sort((a, b) => b.timestamp - a.timestamp)
                .find(e => e.deviceCode === c.deviceCode)
            : null;
        return (
          <Transition
            key={c.id}
            transition='scale-x'
            duration={500}
            mounted={mountedCards.includes(index)}
            timingFunction='ease'
          >
            {styles => (
              <Card
                mt='sm'
                shadow='sm'
                style={styles}
                padding='lg'
                radius='sm'
                withBorder
              >
                <Flex align={'center'} justify='space-between'>
                  <Group>
                    <Tooltip label={c.status}>
                      <Check
                        size={16}
                        style={{ width: 40 }}
                        color={theme.colors.teal[5]}
                      />
                    </Tooltip>
                    <Group>
                      <Text fw={700}>{t('pa_device', 'Dispositivo')}:</Text>
                      <Text fs='italic'>{c.deviceName}</Text>
                      <Text>{c.deviceCode}</Text>
                      <Text fw={700}>{t('pa_user', 'Utente')}:</Text>
                      <Text fs='italic'>{c.patientCode ?? 'n/a'}</Text>
                      <Text fw={700}>Scenario:</Text>
                      <Text fs='italic'>{c.scenario}</Text>
                    </Group>
                    {event && (
                      <Text ml={12}>
                        <Heart size={16} style={{ marginRight: 12 }} />
                        {event.hr}
                        <HeartPulse size={16} style={{ marginRight: 12 }} />
                        {event.hrv}
                      </Text>
                    )}
                  </Group>
                  <Group>
                    <ActionIcon
                      onClick={() => navigate('/completedSessions/' + c.id)}
                    >
                      <Eye size={16} />
                    </ActionIcon>

                    <ActionIcon
                      color={'red'}
                      loading={deleteInProgress === c.id}
                      disabled={deleteInProgress === c.id}
                      onClick={() => confirmDelete(c.id)}
                    >
                      <Trash2 size={16} />
                    </ActionIcon>
                  </Group>
                </Flex>
              </Card>
            )}
          </Transition>
        );
      })}
    </>
  );
}
